import {
    Component,
    effect,
    EffectRef,
    input,
    InputSignal,
    model,
    ModelSignal,
    OnDestroy,
    OnInit,
    untracked,
} from '@angular/core';
import { Invoice } from '../invoice.model';
import { InvoiceService } from '../invoice.service';

import { Constants } from '../../../constants.new';
import { FormBuilder, FormGroup, FormsModule } from '@angular/forms';
import { TaxSavingsDetailsComponent } from './Tax-Savings-Details/taxSavingsDetails.component';
import { MessageModalService } from '../../../UI-Lib/Message-Box/messageModal.service';
import { BusyIndicatorRef, BusyIndicatorService } from '../../../Busy-Indicator';
import { TaxSavingsSummaryComponent } from './Tax-Savings-Summary/taxSavingsSummary.component';
import { InvoiceDetailsService } from './invoiceDetails.service';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { HelpService } from '../../../UI-Lib/Help-Tooltip';
import { INVOICE_DETAILS_HELP } from './invoiceDetails.help';

@Component({
    selector: 'invoice-details',
    templateUrl: './invoiceDetails.component.html',
    styleUrls: ['./invoiceDetails.component.scss'],
    imports: [FormsModule, TaxSavingsDetailsComponent, TaxSavingsSummaryComponent],
    providers: [InvoiceDetailsService],
})
export class InvoiceDetailsComponent implements OnInit, OnDestroy {
    constructor(
        private invoiceService: InvoiceService,
        private _fb: FormBuilder,
        public constants: Constants,
        private readonly _messageModalService: MessageModalService,
        private readonly _busyIndicatorService: BusyIndicatorService,
        private readonly _invoiceDetailsService: InvoiceDetailsService,
        private readonly _helpService: HelpService,
    ) {}

    currentInvoice: ModelSignal<Invoice> = model();
    cachedInvoice: InputSignal<Invoice> = input();
    editMode: InputSignal<boolean> = input();
    loading: ModelSignal<boolean> = model();

    invoiceForm: FormGroup;

    private readonly _destroy$: Subject<void> = new Subject();

    private readonly _loadingEffect: EffectRef = effect(() => {
        const loading = this.loading();
        if (loading) {
            this._invoiceDetailsService.invoiceInitialized = false;
        }
    });

    private readonly _invoiceEffect: EffectRef = effect(() => {
        const invoice = this.currentInvoice();
        if (!invoice) return;

        untracked(() => {
            if (!this.invoiceForm || this._invoiceDetailsService.invoiceInitialized) return;
            this.automatedNoteChange();
        });
    });

    ngOnInit(): void {
        this._helpService.setContent(INVOICE_DETAILS_HELP);

        this.invoiceForm = this._fb.group({
            previousTAV: [null],
            previousMV: [null],
            invoiceTAV: [null],
            invoiceMV: [null],
            siteChangeTAV: [null, { disabled: true }],
            siteChangeMV: [null, { disabled: true }],
            billableTaxRate: [null],
            taxSavingsTotal: [null],
            includeOtherParcels: [null],

            contingencyPct: [null],
            contingencyCap: [null],
            feeAmount: [null],
            fixedFee: [null],
            expenseAmount: [null],
        });

        this.invoiceForm.disable({ emitEvent: false });

        this.invoiceForm
            .get('includeOtherParcels')
            .valueChanges.pipe(takeUntil(this._destroy$))
            .subscribe((x) => {
                this.currentInvoice().includeOtherParcels = x;
                this.automatedNoteChange();
            });

        // Update the invoice with any changes to the form
        this.invoiceForm.valueChanges.pipe(takeUntil(this._destroy$)).subscribe(() => {
            const currentInvoice = this.currentInvoice();

            const formValues = this.invoiceForm.getRawValue();
            Object.keys(this._invoiceDetailsService.overrides).forEach((x) => {
                if (formValues[x] !== undefined && typeof formValues[x] !== 'string') {
                    currentInvoice[x] = formValues[x];
                }
            });
            this.currentInvoice.set({ ...currentInvoice });
            this.automatedNoteChange();
        });

        this._invoiceDetailsService.notes$.pipe(takeUntil(this._destroy$)).subscribe((x) => {
            if (x) {
                this.currentInvoice().notes = x;
            }
        });
    }

    ngOnDestroy(): void {
        this._destroy$.next();
        this._destroy$.complete();
        this._invoiceEffect?.destroy();
        this._loadingEffect?.destroy();
    }

    async refreshCurrentInvoice(): Promise<void> {
        let updateExistingTaxSavingsWorksheet: boolean = true;

        if (
            !this.currentInvoice().isAdHoc &&
            (this._hasMultipleTaxable() || this.currentInvoice().includeTaxSavingsWorksheet)
        ) {
            try {
                await this._messageModalService.confirmYesNo(
                    'Do you want to update any existing Tax Savings Worksheet(s)?',
                    'Confirm',
                );
            } catch {
                updateExistingTaxSavingsWorksheet = false;
            }
        }

        this.loading.set(true);
        this._invoiceDetailsService.invoiceInitialized = false;

        try {
            const refreshed = await this.invoiceService.refreshInvoice(
                this.cachedInvoice(),
                updateExistingTaxSavingsWorksheet,
            );

            this.currentInvoice.set({
                ...refreshed,
                includeTaxSavingsWorksheet: updateExistingTaxSavingsWorksheet
                    ? this._hasMultipleTaxable()
                    : this.currentInvoice().includeTaxSavingsWorksheet,
            });
        } finally {
            this.loading.set(false);
        }
    }

    automatedNoteChange(): void {
        this._invoiceDetailsService.generateNotes(this.currentInvoice());
    }

    async includeTaxSavingsWorksheetChange(isIncludeOtherParcelsChanged: boolean = false): Promise<void> {
        const message = isIncludeOtherParcelsChanged
            ? 'Any existing Tax Savings Worksheet(s) will be deleted and re-added. Do you want to proceed?'
            : 'Any existing Tax Savings Worksheet(s) will be removed. Do you want to proceed?';

        try {
            await this._messageModalService.confirmYesNo(message, 'Confirm');
        } catch {
            if (!isIncludeOtherParcelsChanged) {
                this.currentInvoice().includeTaxSavingsWorksheet = !this.currentInvoice().includeTaxSavingsWorksheet;
            }
            return;
        }

        let busyRef: BusyIndicatorRef;
        try {
            if (this.currentInvoice().includeTaxSavingsWorksheet) {
                if (isIncludeOtherParcelsChanged) {
                    busyRef = this._busyIndicatorService.show({
                        message: 'Removing existing Tax Savings Worksheet(s).',
                    });
                    await this.invoiceService.deleteTaxSavingsWorksheets(this.currentInvoice().invoiceId);
                    await busyRef.hide();
                }

                busyRef = this._busyIndicatorService.show({ message: 'Generating Tax Savings Worksheet.' });
                await this.invoiceService.generateWorksheet(
                    this.currentInvoice().invoiceId,
                    this.currentInvoice().includeOtherParcels,
                );
                this.currentInvoice().hasAttachments = true;
            } else {
                busyRef = this._busyIndicatorService.show({ message: 'Removing existing Tax Savings Worksheet(s).' });
                const hasOtherAttachments = await this.invoiceService.deleteTaxSavingsWorksheets(
                    this.currentInvoice().invoiceId,
                );
                this.currentInvoice().hasAttachments = hasOtherAttachments;
            }

            this.cachedInvoice().includeTaxSavingsWorksheet = this.currentInvoice().includeTaxSavingsWorksheet;
            this.cachedInvoice().hasAttachments = this.currentInvoice().hasAttachments;
            this.automatedNoteChange();
        } catch (e) {
            this.currentInvoice().includeTaxSavingsWorksheet = !this.currentInvoice().includeTaxSavingsWorksheet;
            throw e;
        } finally {
            await busyRef.hide();
        }
    }

    public async otherParcelsChanged(includeOtherSiteParcels: boolean) {
        if (this.currentInvoice().includeTaxSavingsWorksheet) {
            this.currentInvoice().includeOtherParcels = includeOtherSiteParcels;
            await this.includeTaxSavingsWorksheetChange(true);
        }
    }

    private _hasMultipleTaxable(): boolean {
        const taxSavingsInfo = this.currentInvoice().taxSavings;

        return (
            taxSavingsInfo &&
            taxSavingsInfo.some((x) =>
                x.parcelSavingsCalculations.some(
                    (y) => y.appealRevisionCalculations.length > 1 || y.outcomeRevisionCalculations.length > 1,
                ),
            )
        );
    }
}
